import { useEffect, useState } from 'react';
import { FoxyAvatar } from './FoxyAvatar';

interface MilestoneData {
  readonly type: string;
  readonly message: string;
}

interface FoxyCelebrationProps {
  readonly milestone: MilestoneData | null;
  readonly onDismiss: () => void;
}

const AUTO_DISMISS_MS = 5000;

/**
 * Full-screen milestone celebration overlay.
 *
 * - Shows animated fox (128px, celebrating state) + Hebrew milestone message
 * - CSS-only confetti-like decorative elements (no extra deps)
 * - Auto-dismisses after 5 seconds or on click/tap
 * - RTL Hebrew text, mobile-friendly (centered, readable at 360px)
 * - Fade-in/fade-out transitions
 */
export function FoxyCelebration({ milestone, onDismiss }: FoxyCelebrationProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!milestone) {
      setVisible(false);
      return;
    }

    // Trigger fade-in
    requestAnimationFrame(() => setVisible(true));

    // Auto-dismiss
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 300); // Wait for fade-out
    }, AUTO_DISMISS_MS);

    return () => clearTimeout(timer);
  }, [milestone, onDismiss]);

  if (!milestone) return null;

  function handleDismiss() {
    setVisible(false);
    setTimeout(onDismiss, 300);
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={handleDismiss}
      onKeyDown={(e) => { if (e.key === 'Escape') handleDismiss(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Milestone celebration"
    >
      {/* Overlay backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Content */}
      <div
        className="relative z-10 flex flex-col items-center gap-4 rounded-2xl bg-card p-8 mx-4 max-w-sm text-center shadow-xl"
        dir="rtl"
      >
        {/* Decorative sparkles (CSS only) */}
        <div className="absolute -top-3 -start-3 text-2xl animate-bounce">{'✨'}</div>
        <div className="absolute -top-3 -end-3 text-2xl animate-bounce" style={{ animationDelay: '0.2s' }}>{'✨'}</div>
        <div className="absolute -bottom-3 -start-3 text-2xl animate-bounce" style={{ animationDelay: '0.4s' }}>{'🎉'}</div>
        <div className="absolute -bottom-3 -end-3 text-2xl animate-bounce" style={{ animationDelay: '0.6s' }}>{'🎉'}</div>

        {/* Animated fox */}
        <FoxyAvatar state="celebrating" size={128} />

        {/* Message */}
        <p className="text-xl font-bold text-foreground leading-relaxed">
          {milestone.message}
        </p>

        {/* Dismiss hint */}
        <p className="text-xs text-muted-foreground">
          {'\u05DC\u05D7\u05E5/\u05D9 \u05DC\u05E1\u05D2\u05D9\u05E8\u05D4'}
        </p>
      </div>
    </div>
  );
}
